import { Text, UseDisclosureReturn, VStack } from "@chakra-ui/react";
import { Address } from "@syla/shared/types/models/AccountBase";
import React from "react";
import { useForm } from "react-hook-form";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { useMutationUpdateAccount } from "../../../../store/useMutationUpdateAccount";
import { InputField } from "../../../forms/InputField";
import { FormModal } from "../../../modals/FormModal";
import { StateSelectorInputField } from "./StateSelectorInputField";

type EditAddressFormValues = {
  address: Address;
};

export function EditAddressModal({
  currentAddress,
  disclosure,
}: {
  currentAddress: Address;
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const values = React.useMemo<EditAddressFormValues>(
    () => ({
      address: {
        suite: currentAddress.suite ?? "",
        street: currentAddress.street ?? "",
        suburb: currentAddress.suburb ?? "",
        postcode: currentAddress.postcode ?? "",
        state: currentAddress.state ?? "",
      },
    }),
    [
      currentAddress.suite,
      currentAddress.street,
      currentAddress.suburb,
      currentAddress.postcode,
      currentAddress.state,
    ],
  );

  const form = useForm<EditAddressFormValues>({
    values,
    mode: "onTouched",
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const mutationUpdateAccount = useMutationUpdateAccount(accountId);

  const onSubmit = async (data: EditAddressFormValues) => {
    await mutationUpdateAccount.mutateAsync({
      address: {
        street: data.address.street,
        suite: data.address.suite,
        suburb: data.address.suburb,
        state: data.address.state,
        postcode: data.address.postcode,
      },
    });
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title={`${currentAddress.street ? "Edit" : "Add"} address`}
      submitButtonText="Save changes"
      submitCondition="dirty"
      successTitle="Address updated"
      errorTitle="Address update failed"
      onSubmit={onSubmit}
    >
      <Text color="gray.600" mb={6}>
        The address is optional and is only required if you want it displayed on
        the Crypto Tax Report.
      </Text>

      <VStack spacing={4}>
        <InputField
          form={form}
          name="address.suite"
          label="Unit / Apartment"
          helperText="Optional. Include a unit, apartment, suite, or level number."
          placeholder="Unit 21"
          type="text"
          autoFocus
        />

        <InputField
          form={form}
          name="address.street"
          label="Street"
          helperText="Include the street number and street name."
          placeholder="123 Satoshi Street"
          type="text"
          isRequired
        />

        <InputField
          form={form}
          name="address.suburb"
          label="Suburb / City"
          helperText="Enter the suburb, town, or city for the address."
          placeholder="Blocktown"
          type="text"
          isRequired
        />

        <StateSelectorInputField
          form={form}
          name="address.state"
          isRequired
          buttonProps={{
            width: "full",
            maxW: "12em",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />

        <InputField
          maxW="12em"
          form={form}
          name="address.postcode"
          label="Postcode"
          placeholder="4000"
          type="text"
          isRequired
        />
      </VStack>
    </FormModal>
  );
}
